/**
 * useOSBadges — counts dinámicos para badges del sidebar del OS interno.
 *
 * Refresca cada 90s o al volver al tab.
 *
 * Counts:
 *  - mis_tareas:      tareas asignadas a mí en estado accionable
 *  - mi_bandeja:      items pendientes en mi inbox según rol
 *                       EC  → tareas propias en "revision-cliente"
 *                       CCC → sin_asignar + bloqueadas + atrasadas (de /api/ccc/dashboard)
 *                       COM → tareas globales en "para-qc" o "qc-com-pendiente"
 *  - pedidos_cliente: pedidos en estado "pendiente"
 */

import { useEffect, useState, useCallback } from "react";
import { api } from "../api/client.js";
import { useAuth } from "../context/AuthContext.jsx";
import { isActiva } from "./utils.js";

/** Normaliza respuesta de conteo: acepta array (length) o número directo */
function countOf(v) {
  if (Array.isArray(v)) return v.length;
  if (typeof v === "number") return v;
  return 0;
}

export function useOSBadges() {
  const { miembro, isCuenta, isCCC, isCM, isAdmin, isCOO } = useAuth();
  const [badges, setBadges] = useState({
    mis_tareas: 0,
    mi_bandeja: 0,
    pedidos_cliente: 0,
    notif_unread: 0,
    subtareas_pendientes: 0,
    pedidos_agencia: 0,
  });

  const [listo, setListo] = useState(false);   // true tras la primera carga: antes, los ceros no son datos

  const refresh = useCallback(async () => {
    if (!miembro?.id) return;
    try {
      // Fan-out en paralelo — más rápido y resistente a fallos individuales
      const [tareasR, pedidosR, notifR, subsR, chatR] = await Promise.all([
        api.request("GET", `/api/tareas?responsable_id=${miembro.id}`).catch(() => ({ tareas: [] })),
        api.request("GET", "/api/pedidos-cliente").catch(() => ({ pedidos: [] })),
        api.request("GET", `/api/notifications?user_id=${miembro.id}&unread_only=true&limit=1`).catch(() => ({ unread_count: 0 })),
        api.getMisSubtareas({ responsable_id: miembro.id }).catch(() => ({ subtareas: [] })),
      ]);

      const listaMias = tareasR.tareas || tareasR || [];
      const tareasActivasMias = listaMias.filter(t => t && isActiva(t.estado)).length;
      const pedidosPendientes = (pedidosR.pedidos || []).filter(p => p.estado === "pendiente").length;
      const notifUnread = notifR.unread_count || 0;
      // getMisSubtareas ya filtra los estados "done" por default
      const subsCount = (subsR.subtareas || []).length;
      // Mis Tareas total = tareas activas + subs pendientes (no aprobadas)
      const misTareasCount = tareasActivasMias + subsCount;

      // ── Mi Bandeja según rol ─────────────────────────────────────────────
      let miBandejaCount = 0;

      if (isCuenta) {
        miBandejaCount = listaMias.filter(t => t?.estado === "EC Aprobado" || t?.estado === "Cliente Cambios").length;
      } else if (isCCC) {
        const dash = await api.request("GET", "/api/ccc/dashboard").catch(() => null);
        if (dash) miBandejaCount = countOf(dash.sin_asignar) + countOf(dash.bloqueadas) + countOf(dash.atrasadas);
      } else if (isCM) {
        const qc = await api.request("GET", `/api/tareas?estado=${encodeURIComponent("Para QC")}`).catch(() => ({ tareas: [] }));
        miBandejaCount = (qc.tareas || qc || []).filter(t => t?.estado === "Para QC").length;
      }

      // Pedidos de agencia (entre clientes/equipos internos) — solo CEO/COO/CCC/EC
      let pedidosAgenciaCount = 0;
      if (isAdmin || isCOO || isCCC || isCuenta) {
        const pa = await api.request("GET", "/api/pedidos-agencia?estado=pendiente").catch(() => ({ pedidos: [] }));
        pedidosAgenciaCount = (pa.pedidos || []).length;
      }

      setBadges({
        mis_tareas: misTareasCount,
        mi_bandeja: miBandejaCount,
        pedidos_cliente: pedidosPendientes,
        notif_unread: notifUnread,
        subtareas_pendientes: subsCount,
        pedidos_agencia: pedidosAgenciaCount,
      });
      setListo(true);
    } catch (err) {
      console.warn("[useOSBadges]", err.message);
    }
  }, [miembro?.id, isCuenta, isCCC, isCM, isAdmin, isCOO]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") refresh();
    }, 90_000);
    return () => clearInterval(interval);
  }, [refresh]);

  useEffect(() => {
    const onVis = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [refresh]);

  return { badges, refresh, listo };
}
